import { _, helpers, mui, React, ts, ui } from '_core';

import { nestedBarChartFormat } from '../../common/formatters/nested-bar-chart';

type ChartProps = {
  id: string;
  readOnly: boolean;
  widgetKey: string;
  file: string;
  fullScreen: boolean;
  title: string;
  loadWidgetData: (_payload: ts.types.widgets.WidgetGetDataParams) => Promise<ts.types.widgets.WidgetGetDataResponse>;
};

type TabTypes = 'Rank Correlation' | 'Pearson Correlation';

const Chart = ({ id, readOnly, loadWidgetData, file, fullScreen, title }: ChartProps) => {
  const [data, setData] = React.useState<ts.types.widgets.TableData>(null);
  const [error, setError] = React.useState<ts.types.common.ApiError>(null);

  const [tab, setTab] = React.useState<TabTypes>('Pearson Correlation');

  const loadData = async () => {
    try {
      const response = await loadWidgetData({
        data: {
          file,
          category: 'build_date',
          dropna: true,
          columns: ['build_date', tab],
          rename: {
            rank_correlation: 'Rank Correlation',
            pearson_correlation: 'Pearson Correlation',
          },
        },
      });
      if (response) {
        setData(response.data);
      }
    } catch (err) {
      setError(err as ts.types.common.ApiError);
    }
  };

  React.useEffect(() => {
    if (file) loadData();
    return () => setData(null);
  }, [file, tab]);

  const formattedData = React.useMemo(() => {
    if (_.isNil(data)) return null;
    return nestedBarChartFormat(data, 'build_date');
  }, [data]);

  return (
    <mui.core.Box display="flex" flexDirection="column" height="100%">
      <mui.core.Tabs
        value={tab}
        indicatorColor="primary"
        textColor="primary"
        onChange={(e, newTab) => setTab(newTab)}
        aria-label="Cross-Sectional Correlation Tabs"
      >
        <mui.core.Tab label={'Correlation'} value={'Pearson Correlation'} />
        <mui.core.Tab label={'Rank Correlation'} value={'Rank Correlation'} />
      </mui.core.Tabs>
      <mui.core.Box flexGrow={1}>
        {!_.isNil(formattedData) && (
          <ui.NestedBarChart
            id={id}
            data={formattedData?.data}
            count={formattedData?.count}
            category="category"
            yAxisLabel=""
            xAxisLabel=""
            alert={error ? { type: 'error', message: helpers.parseApiError(error) } : undefined}
            fullScreen={fullScreen}
            exportTitle={title}
            readOnly={readOnly}
          />
        )}
      </mui.core.Box>
    </mui.core.Box>
  );
};

export default Chart;
